import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import PageTransition from '../components/ui/PageTransition';
import SectionHeading from '../components/ui/SectionHeading';
import Button from '../components/ui/Button';
import HeroSection from '../components/sections/HeroSection';
import CtaSection from '../components/sections/CtaSection';
import { services } from '../data/services';

const ServicesPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Web Design & Development Services | Consiliuma Nottingham</title>
        <meta name="description" content="Comprehensive web design, development, and maintenance services in Nottingham. From beautiful websites to complex web applications, we deliver solutions that drive growth." />
        <meta name="keywords" content="Web Design Services Nottingham, Web Development Services, Full Stack Development, Website Maintenance" />
        <link rel="canonical" href="/services" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Web Design & Development Services | Consiliuma Nottingham" />
        <meta property="og:description" content="Comprehensive web design, development, and maintenance services in Nottingham. From beautiful websites to complex web applications, we deliver solutions that drive growth." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://consiliuma.co.uk/services" />
        <meta property="og:image" content="https://images.pexels.com/photos/1181298/pexels-photo-1181298.jpeg" />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />

        {/* Schema.org data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Web Design & Development Services | Consiliuma Nottingham",
            "description": "Comprehensive web design, development, and maintenance services in Nottingham.",
            "url": "https://consiliuma.co.uk/services",
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": services.map((service, index) => ({
                "@type": "Service",
                "position": index + 1,
                "name": service.title,
                "description": service.description,
                "provider": {
                  "@type": "Organization",
                  "name": "Consiliuma",
                  "url": "https://consiliuma.co.uk"
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <main>
        <h1 className="sr-only">Web Design & Development Services in Nottingham</h1>

        <HeroSection
          title="Our Services"
          subtitle="Comprehensive web solutions from design to development and ongoing maintenance"
          ctaText="Get a Free Quote"
          ctaLink="/quote"
          backgroundImage="https://images.pexels.com/photos/1181298/pexels-photo-1181298.jpeg"
        />

        {/* Services Overview */}
        <section className="py-16 md:py-24" aria-labelledby="services-heading">
          <div className="container-custom">
            <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Our Core Services</h2>
            <p className="text-xl text-secondary mb-12 text-center">Comprehensive web solutions tailored to your business needs</p>

            <div className="mt-12">
              {services.map((service, index) => (
                <motion.div 
                  key={service.id}
                  className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 mb-16 pb-16 ${index !== services.length - 1 ? 'border-b border-gray-200' : ''}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                >
                  <div className="lg:w-1/2">
                    <div className="flex items-start mb-6">
                      <div className="text-primary mr-4">
                        <service.icon size={40} />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-primary">{service.title}</h3>
                    </div>
                    
                    <div dangerouslySetInnerHTML={{ __html: service.detailedDescription }} className="text-secondary mb-8" />
                    
                    <Button to="/quote" variant="primary">
                      Request a Quote
                    </Button>
                  </div>
                  
                  <div className="lg:w-1/2 bg-white p-8 rounded-lg shadow-md">
                    <h4 className="text-xl font-bold text-primary mb-6">Key Features</h4>
                    <ul className="space-y-3">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <svg className="w-5 h-5 text-primary mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Process */}
        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="process-heading">
          <div className="container-custom">
            <h2 id="process-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Our Service Process</h2>
            <p className="text-xl text-secondary mb-12 text-center">How we deliver exceptional results for every project</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-bold mb-3 text-primary">Discovery</h3>
                <p className="text-secondary">
                  We begin by understanding your business, goals, target audience, and project requirements to create a tailored solution.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-bold mb-3 text-primary">Planning</h3>
                <p className="text-secondary">
                  We develop a comprehensive project plan, including timeline, deliverables, technical specifications, and design concepts.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-bold mb-3 text-primary">Execution</h3>
                <p className="text-secondary">
                  Our British development team brings the plan to life, with regular updates and opportunities for feedback throughout.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-bold mb-3 text-primary">Support</h3>
                <p className="text-secondary">
                  After launch, we provide ongoing maintenance, updates, and support to ensure continued success and optimal performance.
                </p>
              </motion.div>
            </div>

            <div className="text-center mt-12">
              <Button to="/our-process" variant="outline">
                Learn More About Our Process
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24" aria-labelledby="faq-heading">
          <div className="container-custom">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Frequently Asked Questions</h2>
            <p className="text-xl text-secondary mb-12 text-center">Common questions about our services</p>

            <div className="mt-12 max-w-3xl mx-auto">
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">How long does a typical web development project take?</h3>
                <p className="text-secondary">
                  Project timelines vary based on complexity. A simple website might take 4-6 weeks, while complex web applications can take 3-6 months. During our initial consultation, we'll provide a detailed timeline specific to your project requirements.
                </p>
              </motion.div>

              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">What does your monthly maintenance service include?</h3>
                <p className="text-secondary">
                  Our maintenance service includes security updates, performance optimization, content updates, regular backups, technical support, and monthly analytics reporting. We offer different tiers based on your specific needs and website complexity.
                </p>
              </motion.div>

              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Do you work with clients outside of Nottingham, Arizona, and SE Asia?</h3>
                <p className="text-secondary">
                  Absolutely! While we have physical offices in these locations, we work with clients worldwide. Our international team and digital workflow allow us to collaborate effectively with businesses anywhere, providing the same high-quality service regardless of location.
                </p>
              </motion.div>

              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">How do you handle the SEO for new websites?</h3>
                <p className="text-secondary">
                  SEO is built into our development process. We implement technical SEO best practices, optimize site structure, ensure mobile responsiveness, and set up proper meta tags. For more comprehensive SEO needs, we also offer ongoing optimization services to improve rankings and drive organic traffic.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection
          title="Ready to Start Your Web Project?"
          subtitle="Contact us today for a free consultation and quote."
          buttonText="Get a Free Quote"
          buttonLink="/quote"
        />
      </main>
    </PageTransition>
  );
};

export default ServicesPage;